import React, { useCallback, useEffect, useState } from 'react';
import { SearchResults } from './components/SearchResults';
import { HorseDetailView } from './components/HorseDetailView';
import { MediaDetailView } from './components/MediaDetailView';
import { UserSettings } from './components/UserSettings';
import { CompetitionsSearch } from './components/CompetitionsSearch';
import { CompetitionDetailView } from './components/CompetitionDetailView';
import { EventDetailView } from './components/EventDetailView';
import { InboxView } from './components/InboxView';
import { InboxDetailView } from './components/InboxDetailView';
import { Login } from './components/Login';
import { AppHeader } from './components/AppHeader';
import { BottomNav } from './components/BottomNav';
import { apiService } from './services/api';
import { initPushNotifications } from './services/pushNotifications';
import { urlParams, ViewMode } from './utils/urlParams';
import { UserRead } from './types/api';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<ViewMode>('all');
  const [currentHorseId, setCurrentHorseId] = useState<string | null>(null);
  const [currentMediaId, setCurrentMediaId] = useState<string | null>(null);
  const [currentCompetitionId, setCurrentCompetitionId] = useState<string | null>(null);
  const [currentEventId, setCurrentEventId] = useState<string | null>(null);
  const [currentInboxMessageId, setCurrentInboxMessageId] = useState<string | null>(null);
  const [user, setUser] = useState<UserRead | null>(null);
  const [currentHorseTitle, setCurrentHorseTitle] = useState<string | null>(null);
  const [currentCompetitionTitle, setCurrentCompetitionTitle] = useState<string | null>(null);
  const [currentEventTitle, setCurrentEventTitle] = useState<string | null>(null);
  const [currentInboxTitle, setCurrentInboxTitle] = useState<string | null>(null);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const checkAuth = async () => {
      const authenticated = apiService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        try {
          const userData = await apiService.getCurrentUser();
          setUser(userData);
        } catch (error) {
          console.error('Failed to load user:', error);
          apiService.logout();
          setIsAuthenticated(false);
          setUser(null);
        }
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  useEffect(() => {
    if (user && (window as any).hj) {
      (window as any).hj('identify', user.id, {
        email: user.email,
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      initPushNotifications().catch((err) =>
        console.error('Push notification init failed:', err)
      );
    }
  }, [user]);

  const refreshInboxUnreadCount = useCallback(async () => {
    if (!apiService.isAuthenticated()) {
      setInboxUnreadCount(0);
      return;
    }

    try {
      const { count } = await apiService.getInboxUnreadCount();
      setInboxUnreadCount(count);
    } catch (err) {
      console.error('Failed to load inbox unread count:', err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      refreshInboxUnreadCount();
    } else {
      setInboxUnreadCount(0);
    }
  }, [refreshInboxUnreadCount, user]);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';

    const handleUrlChange = () => {
      const viewMode = urlParams.getViewModeFromUrl();
      const horseId = urlParams.getHorseIdFromUrl();
      const mediaId = urlParams.getMediaIdFromUrl();
      const competitionId = urlParams.getCompetitionIdFromUrl();
      const eventId = urlParams.getEventIdFromUrl();
      const inboxMessageId = urlParams.getInboxMessageIdFromUrl();
      
      setCurrentView(viewMode);
      setCurrentHorseId(horseId);
      setCurrentMediaId(mediaId);
      setCurrentCompetitionId(competitionId);
      setCurrentEventId(eventId);
      setCurrentInboxMessageId(inboxMessageId);

      window.scrollTo(0, 0);
    };

    // Initial load
    handleUrlChange();

    // Listen for popstate events (back/forward navigation)
    const handlePopState = () => {
      handleUrlChange();
    };

    // Listen for custom urlchange events (programmatic navigation)
    const handleUrlChangeEvent = () => {
      handleUrlChange();
    };

    const handleUnauthorized = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('urlchange', handleUrlChangeEvent);
    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('urlchange', handleUrlChangeEvent);
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const handleLoginSuccess = async () => {
    // signed in from /signin: continue at the home view
    if (window.location.pathname === '/signin') {
      window.history.replaceState({}, '', '/');
      window.dispatchEvent(new CustomEvent('urlchange'));
    }
    setIsAuthenticated(true);
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user after login:', error);
    }
  };

  const handleLogout = () => {
    apiService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const handleNavigateHome = () => {
    urlParams.navigateBack();
  };

  const handleLogoClick = () => {
    urlParams.resetToHome();
  };

  useEffect(() => {
    if (currentView !== 'horse-detail') setCurrentHorseTitle(null);
    if (currentView !== 'competition-detail') setCurrentCompetitionTitle(null);
    if (currentView !== 'event-detail') setCurrentEventTitle(null);
    if (currentView !== 'inbox-detail') setCurrentInboxTitle(null);
  }, [currentView]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Signed-out visitors at / are sent to the video analysis page before the app starts (src/index.tsx);
    // everywhere else, including /signin and deep links like a shared horse page, they get the sign-in form.
    return (
      <div className="App">
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  // Render horse detail view
  if (currentView === 'horse-detail' && currentHorseId) {
    return (
      <div className="App pb-16 lg:pb-0 lg:pl-60 lg:max-w-[1400px] lg:mx-auto">
        <AppHeader
          user={user}
          onLogout={handleLogout}
          onLogoClick={handleLogoClick}
          showBackButton={true}
          variant="minimal"
          onBackClick={handleNavigateHome}
          title={currentHorseTitle || undefined}
          backButtonText="Back"
        />
        <HorseDetailView 
          horseId={currentHorseId} 
          onTitleChange={setCurrentHorseTitle}
          onNavigateBack={handleNavigateHome}
        />
        <BottomNav currentView={currentView} inboxUnreadCount={inboxUnreadCount} />
      </div>
    );
  }

  // Render media detail view
  if (currentView === 'media-detail' && currentMediaId) {
    return (
      <div className="App pb-16 lg:pb-0 lg:pl-60 lg:max-w-[1400px] lg:mx-auto">
        <AppHeader
          user={user}
          onLogout={handleLogout}
          onLogoClick={handleLogoClick}
          showBackButton={true}
          onBackClick={handleNavigateHome}
          backButtonText="Back to Media"
        />
        <MediaDetailView 
          mediaId={currentMediaId} 
          onNavigateBack={handleNavigateHome}
        />
        <BottomNav currentView={currentView} inboxUnreadCount={inboxUnreadCount} />
      </div>
    );
  }

  // Render event detail view
  if (currentView === 'event-detail' && currentEventId) {
    return (
      <div className="App pb-16 lg:pb-0 lg:pl-60 lg:max-w-[1400px] lg:mx-auto">
        <AppHeader
          user={user}
          onLogout={handleLogout}
          onLogoClick={handleLogoClick}
          showBackButton={true}
          variant="minimal"
          onBackClick={handleNavigateHome}
          title={currentEventTitle || 'Event'}
          backButtonText="Back"
        />
        <EventDetailView
          eventId={currentEventId}
          onNavigateBack={handleNavigateHome}
          onTitleChange={setCurrentEventTitle}
        />
        <BottomNav currentView={currentView} inboxUnreadCount={inboxUnreadCount} />
      </div>
    );
  }

  // Render competition detail view
  if (currentView === 'competition-detail' && currentCompetitionId) {
    return (
      <div className="App pb-16 lg:pb-0 lg:pl-60 lg:max-w-[1400px] lg:mx-auto">
        <AppHeader
          user={user}
          onLogout={handleLogout}
          onLogoClick={handleLogoClick}
          showBackButton={true}
          variant="minimal"
          onBackClick={handleNavigateHome}
          title={currentCompetitionTitle || 'Class Details'}
          backButtonText="Back"
        />
        <CompetitionDetailView
          competitionId={currentCompetitionId}
          onNavigateBack={handleNavigateHome}
          onTitleChange={setCurrentCompetitionTitle}
        />
        <BottomNav currentView={currentView} inboxUnreadCount={inboxUnreadCount} />
      </div>
    );
  }

  if (currentView === 'inbox-detail' && currentInboxMessageId) {
    return (
      <div className="App pb-16 lg:pb-0 lg:pl-60 lg:max-w-[1400px] lg:mx-auto">
        <AppHeader
          user={user}
          onLogout={handleLogout}
          onLogoClick={handleLogoClick}
          showBackButton={true}
          variant="minimal"
          onBackClick={() => urlParams.navigateToInbox()}
          title={currentInboxTitle || 'Inbox'}
          backButtonText="Back to Inbox"
        />
        <InboxDetailView
          messageId={currentInboxMessageId}
          onTitleChange={setCurrentInboxTitle}
          onInboxChanged={refreshInboxUnreadCount}
        />
        <BottomNav currentView={currentView} inboxUnreadCount={inboxUnreadCount} />
      </div>
    );
  }

  return (
    <div className="App pb-16 lg:pb-0 lg:pl-60 lg:max-w-[1400px] lg:mx-auto">
      {currentView === 'competitions' ? (
        <CompetitionsSearch />
      ) : currentView === 'inbox' ? (
        <InboxView onInboxChanged={refreshInboxUnreadCount} />
      ) : currentView === 'account-setting' ? (
        <>
          <AppHeader
            user={user}
            onLogout={handleLogout}
            onLogoClick={handleLogoClick}
            variant="full"
          />
          <UserSettings initialUser={user} />
        </>
      ) : (
        <SearchResults />
      )}
      <BottomNav currentView={currentView} inboxUnreadCount={inboxUnreadCount} />
    </div>
  );
}

export default App;
